import type { Table } from "@tanstack/react-table";
import { Download } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

interface DataTableExportExcelProps<TData> {
	table: Table<TData>;
	filename?: string;
}

function DataTableExportExcel<TData>({
	table,
	filename = "export",
}: DataTableExportExcelProps<TData>) {
	const [isExporting, setIsExporting] = useState(false);

	const handleExport = async () => {
		setIsExporting(true);
		try {
			// Get visible columns (with accessorFn, excluding select/actions columns)
			const visibleColumns = table
				.getAllColumns()
				.filter(
					(column) =>
						column.getIsVisible() &&
						typeof column.accessorFn !== "undefined",
				);

			const columnNames = visibleColumns.map(
				(col) =>
					(col.columnDef.meta as { label?: string })?.label ?? col.id,
			);

			// Get sorted/filtered rows from the table
			const rows = table.getFilteredRowModel().rows;

			const data = rows.map((row) =>
				visibleColumns.map((col) => {
					const meta = col.columnDef.meta as { label?: string; textValue?: (row: TData) => string } | undefined;
					if (meta?.textValue) {
						return meta.textValue(row.original);
					}
					const value = row.getValue(col.id);
					if (value === null || value === undefined) return "";
					if (value instanceof Date) return value.toLocaleDateString("fr-FR");
					return String(value);
				}),
			);

			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/export/excel`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
					body: JSON.stringify({
						columns: columnNames,
						data,
						sheetName: filename,
					}),
				},
			);

			if (!response.ok) throw new Error("Erreur lors de l'export");

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${filename}.xlsx`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			toast.success("Export Excel téléchargé");
		} catch {
			toast.error("Erreur lors de l'export Excel");
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<Button
			variant="outline"
			size="sm"
			className="ml-auto h-8"
			onClick={handleExport}
			disabled={isExporting}
		>
			<Download className="size-3.5" />
			{isExporting ? "Export..." : "Excel"}
		</Button>
	);
}

export { DataTableExportExcel };
